import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const NAMES = ['María', 'José', 'Carmen', 'Luis', 'Ana', 'Carlos', 'Rosa', 'Miguel', 'Elena', 'Pedro', 'Guadalupe', 'Francisco', 'Teresa', 'Roberto', 'Patricia', 'Jorge', 'Isabel', 'Ricardo', 'Sofía', 'Antonio', 'Claudia', 'Fernando', 'Lucía', 'Alejandro', 'Gabriela', 'Raúl', 'Mónica', 'Eduardo', 'Silvia', 'Héctor', 'Laura', 'Sergio', 'Adriana', 'Manuel', 'Verónica', 'Arturo', 'Diana', 'Óscar', 'Beatriz', 'Javier'];
const CITIES = ['Houston, TX', 'Los Ángeles, CA', 'Miami, FL', 'San Antonio, TX', 'Chicago, IL', 'Dallas, TX', 'Phoenix, AZ', 'San Diego, CA', 'El Paso, TX', 'Denver, CO', 'Las Vegas, NV', 'Orlando, FL', 'Tampa, FL', 'Sacramento, CA', 'Albuquerque, NM', 'Tucson, AZ', 'Austin, TX', 'San José, CA', 'Fresno, CA', 'Newark, NJ'];
interface Product {
  title: string;
  editionLabel: string;
  priceText: string;
  thumbnailUrl: string;
}
interface ToastData {
  id: string;
  name: string;
  city: string;
  product: Product;
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [recentNames, setRecentNames] = useState<string[]>([]);
  const prefersReducedMotion = useReducedMotion();
  // 1) Scan the page for products
  useEffect(() => {
    const scanProducts = () => {
      const foundProducts: Product[] = [];
      // Try to find the main product on the page
      const priceCard = document.getElementById('price-card');
      if (priceCard) {
        const title = 'Protocolo SOS';
        const editionLabel = 'Edición Digital + 3 Bonos';
        // Try to find price
        const priceMatch = priceCard.innerText.match(/\$\d+(\.\d{2})?/);
        const priceText = priceMatch ? priceMatch[0] : '$12.89';
        // Try to find image
        const img = priceCard.querySelector('img');
        const thumbnailUrl = img?.src || 'https://cdn.magicpatterns.com/uploads/hfEB98qtYYCQobk9QVTtMz/CamScanner_3-18-26_08.17_page-0001.jpg';
        foundProducts.push({
          title,
          editionLabel,
          priceText,
          thumbnailUrl
        });
      }
      setProducts(foundProducts);
    };
    // Small delay to ensure DOM is fully rendered
    setTimeout(scanProducts, 1000);
  }, []);
  const getRandomProduct = useCallback((): Product => {
    if (products.length > 0) {
      return products[Math.floor(Math.random() * products.length)];
    }
    // Fallback
    const rand = Math.random();
    if (rand < 0.5) {
      return {
        title: 'Protocolo SOS',
        editionLabel: 'Pack Esencial',
        priceText: '$6.39',
        thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/hfEB98qtYYCQobk9QVTtMz/CamScanner_3-18-26_08.17_page-0001.jpg'
      };
    } else if (rand < 0.85) {
      return {
        title: 'Protocolo SOS',
        editionLabel: 'Pack Completo',
        priceText: '$15.39',
        thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/hfEB98qtYYCQobk9QVTtMz/CamScanner_3-18-26_08.17_page-0001.jpg'
      };
    } else {
      return {
        title: 'Protocolo SOS',
        editionLabel: 'Pack Premium',
        priceText: '$24.39',
        thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/hfEB98qtYYCQobk9QVTtMz/CamScanner_3-18-26_08.17_page-0001.jpg'
      };
    }
  }, [products]);
  const getRandomName = useCallback(() => {
    let availableNames = NAMES.filter(name => !recentNames.includes(name));
    if (availableNames.length === 0) {
      availableNames = NAMES; // Fallback if all names used recently
    }
    const name = availableNames[Math.floor(Math.random() * availableNames.length)];
    setRecentNames(prev => {
      const newHistory = [...prev, name];
      if (newHistory.length > 10) newHistory.shift();
      return newHistory;
    });
    return name;
  }, [recentNames]);
  const getRandomCity = () => CITIES[Math.floor(Math.random() * CITIES.length)];
  // 3) Behavior & Timing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const showNextToast = () => {
      const newToast: ToastData = {
        id: Math.random().toString(36).substring(7),
        name: getRandomName(),
        city: getRandomCity(),
        product: getRandomProduct()
      };
      setToast(newToast);
      // Hide after 4 seconds
      setTimeout(() => {
        setToast(null);
        // Schedule next toast between 8-15 seconds
        const nextDelay = Math.floor(Math.random() * (15000 - 8000 + 1) + 8000);
        timeoutId = setTimeout(showNextToast, nextDelay);
      }, 4000);
    };
    // First toast after 3 seconds
    timeoutId = setTimeout(showNextToast, 3000);
    return () => clearTimeout(timeoutId);
  }, [getRandomName, getRandomProduct]);
  const animationProps = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 50
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 20
    },
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25
    }
  };
  return <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center pointer-events-none px-4" data-id="element-383">
      <AnimatePresence mode="wait" data-id="element-384">
        {toast && <motion.div key={toast.id} {...animationProps} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 p-3 md:p-4 flex items-center gap-3 md:gap-4 max-w-md w-full pointer-events-auto" data-id="element-385">
            <div className="shrink-0 relative" data-id="element-386">
              <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover border border-gray-100 shadow-sm" data-id="element-387" />
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5" data-id="element-388">
                <CheckCircle2 className="w-4 h-4 text-medical-green fill-medical-green/10" data-id="element-389" />
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-390">
              <p className="text-sm md:text-base text-gray-800 font-medium mb-0.5 leading-tight truncate" data-id="element-391">
                <span className="font-bold" data-id="element-392">{toast.name}</span> en{' '}
                <span className="font-bold" data-id="element-393">{toast.city}</span>
              </p>
              <p className="text-xs md:text-sm text-gray-500 mb-0 leading-tight truncate" data-id="element-394">
                acaba de comprar {toast.product.editionLabel} —{' '}
                <span className="font-semibold text-medical-green" data-id="element-395">
                  {toast.product.priceText}
                </span>
              </p>
              <p className="text-[10px] text-gray-400 mt-1 mb-0 flex items-center gap-1" data-id="element-396">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-397"></span>
                Compra verificada
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}